import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Fail-closed for native release builds: a packaged iOS app must never point at a preview or
// localhost API. If it does, surface a fatal error at boot rather than silently talking to dev.
if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()
    && /preview|localhost|127\.0\.0\.1/.test(BACKEND_URL || "")) {
  throw new Error("Release build misconfigured: non-production API URL (" + BACKEND_URL + ")");
}

const TOKEN_KEY = "leadctrl_token";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && onUnauthorized) onUnauthorized();
    return Promise.reject(error);
  }
);

/** Normalise our error envelope { error: { code, message } } into a string. */
export function apiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const data = error?.response?.data;
  if (data?.error?.message) return data.error.message;
  if (typeof data?.detail === "string") return data.detail;
  return error?.message || fallback;
}

// ---- endpoint helpers ----
export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
};

export const enumsApi = {
  all: () => api.get("/enums"),
};

export const metaApi = {
  get: () => api.get("/meta"),
};

export const orgsApi = {
  list: (params) => api.get("/organisations", { params }),
  get: (id) => api.get(`/organisations/${id}`),
  create: (body) => api.post("/organisations", body),
  update: (id, body) => api.put(`/organisations/${id}`, body),
  remove: (id) => api.delete(`/organisations/${id}`),
};

export const locationsApi = {
  listForOrg: (orgId) => api.get(`/organisations/${orgId}/locations`),
  listAll: (params) => api.get("/locations", { params }),
  create: (orgId, body) => api.post(`/organisations/${orgId}/locations`, body),
  update: (id, body) => api.put(`/locations/${id}`, body),
  geocode: (id, force = false) => api.post(`/locations/${id}/geocode${force ? "?force=true" : ""}`),
};

export const externalEstablishmentsApi = {
  get: (extId) => api.get(`/external-establishments/${extId}`),
  convert: (extId, identity = {}) => api.post(`/external-establishments/${extId}/convert`, identity),
};

export const planTodayApi = {
  compute: (body) => api.post(`/plan-today`, body),
  remove: (id) => api.delete(`/locations/${id}`),
};

export const pushDevicesApi = {
  register: (body) => api.post(`/push-devices`, body),
  list: () => api.get(`/push-devices`),
  patch: (id, body) => api.patch(`/push-devices/${id}`, body),
  revoke: (id) => api.delete(`/push-devices/${id}`),
  getPrefs: () => api.get(`/me/notification-preferences`),
  setPrefs: (categories) => api.patch(`/me/notification-preferences`, { categories }),
};

export const contactsApi = {
  listForOrg: (orgId) => api.get(`/organisations/${orgId}/contacts`),
  create: (orgId, body) => api.post(`/organisations/${orgId}/contacts`, body),
  update: (id, body) => api.put(`/contacts/${id}`, body),
  remove: (id) => api.delete(`/contacts/${id}`),
};

export const productsApi = {
  list: (params) => api.get("/products", { params }),
};

export const usersApi = {
  list: () => api.get("/users"),
  create: (body) => api.post("/users", body),
  update: (id, body) => api.patch(`/users/${id}`, body),
  updateMe: (body) => api.patch("/users/me", body),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  reactivate: (id) => api.post(`/users/${id}/reactivate`),
  getHomeLocation: () => api.get("/users/me/home-location"),
  setHomeLocation: (body) => api.put("/users/me/home-location", body),
  myTerritories: () => api.get("/users/me/territories"),
  assignTerritory: (id, territory_id) => api.post(`/users/${id}/territories`, { territory_id }),
  unassignTerritory: (id, tid) => api.delete(`/users/${id}/territories/${tid}`),
};

export const invitationsApi = {
  list: (status = "pending") => api.get("/users/invitations", { params: { status } }),
  create: (body) => api.post("/users/invitations", body),
  revoke: (id) => api.post(`/users/invitations/${id}/revoke`),
  accept: (token, password, name) => api.post("/auth/accept-invitation", { token, password, name }),
};

export const availabilityApi = {
  list: (params) => api.get("/availability", { params }),
  create: (body, override = false) => api.post("/availability", body, { params: override ? { override: true } : {} }),
  update: (id, body, override = false) => api.patch(`/availability/${id}`, body, { params: override ? { override: true } : {} }),
  remove: (id) => api.delete(`/availability/${id}`),
  nextFieldDay: () => api.get("/availability/next-field-day"),
};

export const signalsApi = {
  list: (params) => api.get("/signals", { params }),
  assign: (id, user_id) => api.post(`/signals/${id}/assign`, { user_id }),
};

export const notificationsApi = {
  list: (params) => api.get("/notifications", { params }),
  summary: () => api.get("/notifications/summary"),
  read: (id) => api.post(`/notifications/${id}/read`),
  dismiss: (id) => api.post(`/notifications/${id}/dismiss`),
  snooze: (id, until) => api.post(`/notifications/${id}/snooze`, { until }),
};

export const notificationPrefsApi = {
  get: () => api.get("/notification-preferences"),
  update: (body) => api.patch("/notification-preferences", body),
};

export const enrichmentApi = {
  snapshot: (orgId) => api.get(`/organisations/${orgId}/enrichment`),
  run: (orgId) => api.post(`/organisations/${orgId}/enrichment/run`),
  verify: (orgId, factId) => api.post(`/organisations/${orgId}/enrichment/${factId}/verify`),
  unverify: (orgId, factId) => api.post(`/organisations/${orgId}/enrichment/${factId}/unverify`),
};

export const productAssessmentsApi = {
  list: (orgId) => api.get(`/organisations/${orgId}/product-assessments`),
  recompute: (orgId) => api.post(`/organisations/${orgId}/product-assessments`),
  verify: (orgId, code) => api.post(`/organisations/${orgId}/product-assessments/${code}/verify`),
  unverify: (orgId, code) => api.post(`/organisations/${orgId}/product-assessments/${code}/unverify`),
};

export const monitoredAreasApi = {
  list: () => api.get("/monitored-areas"),
  health: () => api.get("/monitored-areas/health"),
  create: (body) => api.post("/monitored-areas", body),
  update: (id, body) => api.patch(`/monitored-areas/${id}`, body),
  remove: (id) => api.delete(`/monitored-areas/${id}`),
  scan: (id) => api.post(`/monitored-areas/${id}/scan`),
};

export const venueRefreshApi = {
  refresh: (body) => api.post("/admin/scan-areas/seed-from-postcode", body),
};

export const territoriesApi = {
  list: () => api.get("/territories"),
  create: (body) => api.post("/territories", body),
  update: (id, body) => api.patch(`/territories/${id}`, body),
  remove: (id) => api.delete(`/territories/${id}`),
  stats: (id) => api.get(`/territories/${id}/stats`),
  recompute: (id) => api.post(`/territories/${id}/recompute`),
  gaps: () => api.get("/territories/gaps"),
};

export const recommendationsApi = {
  channel: (orgId) => api.get(`/organisations/${orgId}/recommended-channel`),
};

export const hotspotsApi = {
  list: (status) => api.get("/hotspots", { params: status ? { status } : {} }),
  get: (id) => api.get(`/hotspots/${id}`),
  prospects: (id) => api.get(`/hotspots/${id}/prospects`),
  recompute: () => api.post("/hotspots/recompute"),
  snooze: (id, until) => api.post(`/hotspots/${id}/snooze`, { snoozed_until: until }),
  dismiss: (id) => api.post(`/hotspots/${id}/dismiss`),
  assign: (id, body) => api.post(`/hotspots/${id}/assign`, body),
};

export const diagnosticsApi = {
  page: (params) => api.get("/admin/diagnostics/webintent", { params }),
  preflight: (params) => api.get("/admin/diagnostics/webintent/preflight", { params }),
  events: (params) => api.get("/admin/diagnostics/webintent/events", { params }),
  event: (id, params) => api.get(`/admin/diagnostics/webintent/events/${id}`, { params }),
  setSecret: (id, body) => api.post(`/admin/ingest-sites/${id}/set-secret`, body),
};

export const webIntentApi = {
  sites: () => api.get("/web-ingest-sites"),
  status: () => api.get("/web-ingest-sites/status"),
  createSite: (body) => api.post("/web-ingest-sites", body),
  rotateSecret: (id) => api.post(`/web-ingest-sites/${id}/rotate-secret`),
};

export const tripsApi = {
  list: () => api.get("/trips"),
  get: (id) => api.get(`/trips/${id}`),
  plan: (body) => api.post("/trips/plan", body),
  patch: (id, body) => api.patch(`/trips/${id}`, body),
  approve: (id, body) => api.post(`/trips/${id}/approve`, body),
};

export const teamApi = {
  get: () => api.get("/team"),
  availability: (params) => api.get("/team/availability", { params }),
};

export const assignApi = {
  organisation: (id, body) => api.post(`/organisations/${id}/assign`, body),
  claim: (id) => api.post(`/organisations/${id}/claim`),
  history: (id) => api.get(`/organisations/${id}/assignment-history`),
  opportunity: (id, body) => api.post(`/opportunities/${id}/reassign`, body),
  task: (id, body) => api.post(`/tasks/${id}/reassign`, body),
  visit: (id, body) => api.post(`/visits/${id}/reassign`, body),
};

export const exampleDataApi = {
  load: () => api.post("/example-data/load"),
  unload: () => api.post("/example-data/unload"),
};

export const todayApi = {
  get: () => api.get("/today"),
};

export const opportunitiesApi = {
  stages: () => api.get("/opportunities/stages"),
  listForOrg: (orgId) => api.get(`/organisations/${orgId}/opportunities`),
  create: (orgId, body) => api.post(`/organisations/${orgId}/opportunities`, body),
  update: (id, body) => api.patch(`/opportunities/${id}`, body),
  transition: (id, stage) => api.post(`/opportunities/${id}/stage`, { stage }),
  close: (id, body) => api.post(`/opportunities/${id}/close`, body),
};

export const activitiesApi = {
  listForOrg: (orgId, limit = 100) => api.get(`/organisations/${orgId}/activities`, { params: { limit } }),
  create: (orgId, body) => api.post(`/organisations/${orgId}/activities`, body),
  correct: (id, body) => api.post(`/activities/${id}/correction`, body),
};

export const visitsApi = {
  listForOrg: (orgId) => api.get(`/organisations/${orgId}/visits`),
  create: (orgId, body) => api.post(`/organisations/${orgId}/visits`, body),
  patch: (id, body) => api.patch(`/visits/${id}`, body),
  setState: (id, state) => api.post(`/visits/${id}/state`, { state }),
  outcome: (id, body) => api.post(`/visits/${id}/outcome`, body),
};

export const tripSuggestionsApi = {
  get: (visitId) => api.get(`/visits/${visitId}/trip-suggestion`),
  compute: (visitId) => api.post(`/visits/${visitId}/trip-suggestion/compute`),
  approve: (id, body) => api.post(`/trip-suggestions/${id}/approve`, body),
  dismiss: (id) => api.post(`/trip-suggestions/${id}/dismiss`),
  removeCandidate: (id, candId) => api.post(`/trip-suggestions/${id}/remove-candidate/${candId}`),
};

export const followupApi = {
  rules: () => api.get("/followup/rules"),
  accept: (visitId) => api.post(`/visits/${visitId}/followup/accept`),
  snooze: (visitId) => api.post(`/visits/${visitId}/followup/snooze`),
  dismiss: (visitId) => api.post(`/visits/${visitId}/followup/dismiss`),
};

export const tasksApi = {
  list: (params) => api.get("/tasks", { params }),
  listForOrg: (orgId) => api.get(`/organisations/${orgId}/tasks`),
  create: (body) => api.post("/tasks", body),
  update: (id, body) => api.patch(`/tasks/${id}`, body),
  complete: (id) => api.post(`/tasks/${id}/complete`),
  cancel: (id) => api.post(`/tasks/${id}/cancel`),
};

export const suppressionsApi = {
  get: (orgId) => api.get(`/organisations/${orgId}/suppression`),
  history: (orgId) => api.get(`/organisations/${orgId}/suppression/history`),
  apply: (orgId, body) => api.post(`/organisations/${orgId}/suppression`, body),
  lift: (orgId) => api.post(`/organisations/${orgId}/suppression/lift`),
};

export const auditApi = {
  list: (params) => api.get("/audit", { params }),
};

export const discoveryApi = {
  nearby: (params) => api.get("/discovery/prospects/nearby", { params }),
  viewport: (params) => api.get("/discovery/prospects", { params }),
  byPostcode: (params) => api.get("/discovery/prospects/by-postcode", { params }),
  businessTypes: () => api.get("/discovery/business-types"),
  establishment: (fsaId) => api.get(`/discovery/establishments/${fsaId}`),
  addToLeads: (fsaId, body) => api.post(`/discovery/prospects/${fsaId}/add-to-leads`, body),
  refresh: (fsaId) => api.post(`/discovery/establishments/${fsaId}/refresh`),
  history: (fsaId) => api.get(`/discovery/establishments/${fsaId}/history`),
};

export const dedupApi = {
  reviews: (status = "pending") => api.get("/dedup/reviews", { params: { status } }),
  resolve: (id, body) => api.post(`/dedup/reviews/${id}/resolve`, body),
};

export const followupsApi = {
  list: (status = "pending") => api.get("/followups", { params: { status } }),
  accept: (id, body = {}) => api.post(`/followups/${id}/accept`, body),
  decline: (id, body = {}) => api.post(`/followups/${id}/decline`, body),
};

export const chReviewApi = {
  list: (status = "pending") => api.get("/companies-house/reviews", { params: { status } }),
  get: (id) => api.get(`/companies-house/reviews/${id}`),
  approve: (id, body = {}) => api.post(`/companies-house/reviews/${id}/approve`, body),
  reject: (id, body) => api.post(`/companies-house/reviews/${id}/reject`, body),
  searchAlternative: (id, query) => api.post(`/companies-house/reviews/${id}/search-alternative`, { query }),
};

export const pilotApi = {
  status: () => api.get("/pilot/status"),
  dashboard: () => api.get("/admin/pilot/dashboard"),
  analytics: (params = {}) => api.get("/pilot/analytics", { params }),
};

export const feedbackApi = {
  send: (body) => api.post("/feedback", body),
  list: (feature) => api.get("/feedback", { params: feature ? { feature } : {} }),
};

export const retentionApi = {
  get: () => api.get("/retention/policy"),
  patch: (body) => api.patch("/retention/policy", body),
};

export function isDedupReview(error) {
  return error?.response?.status === 409 && error?.response?.data?.error?.code === "DEDUP_REVIEW_REQUIRED";
}

export function isSuppressionOverride(error) {
  return error?.response?.data?.error?.code === "SUPPRESSED_OVERRIDE_REQUIRED";
}

export function isAvailabilityConflict(error) {
  return error?.response?.status === 409 && error?.response?.data?.error?.code === "AVAILABILITY_CONFLICT";
}
